import React, { FunctionComponent, useEffect, useState } from "react";

import cls from "classnames";
import moment from "moment";
import { Row, Col, Select, List, Tag, Switch, Spin, Alert, Button, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { MainLayout } from "../../common/Layouts/Main";
import { CogIcon } from "../../common/CogIcon";
import { useDebounce } from "../../utils/useDebounce";
import { useAvailableHeight } from "../../utils/useAvailableHeight";
import { copyToClipboard } from "../../utils/clipboard";
import {
  fetchUsersPaginated,
  selectUsersPaginatedList,
  selectAreUsersPaginatedFetching,
} from "../../../features/users/usersPaginatedSlice";
import { IUser } from "../../../features/users/usersSlice";
import {
  fetchProgressAuditCoursesByUsername,
  selectProgressAuditCourses,
  selectIsFetchingProgressAuditCourses,
  selectHasProgressAuditCoursesError,
  clearProgressAuditCourses,
  IProgressAuditCourseSummary,
} from "../../../features/progressAudit/progressAuditCoursesSlice";
import {
  fetchProgressAuditTracesByProgressId,
  selectProgressAuditTraces,
  selectIsFetchingProgressAuditTraces,
  selectHasProgressAuditTracesError,
  clearProgressAuditTraces,
  IProgressAuditTrace,
} from "../../../features/progressAudit/progressAuditTracesSlice";
import {
  fetchProgressAuditFullByProgressId,
  selectProgressAuditFull,
  clearProgressAuditFull,
} from "../../../features/progressAudit/progressAuditFullSlice";
import {
  fetchProgressAuditEvaluationResults,
  selectProgressAuditEvaluationResults,
  selectIsFetchingProgressAuditEvaluationResults,
  clearProgressAuditEvaluationResults,
} from "../../../features/progressAudit/progressAuditEvaluationResultsSlice";
import { fetchEvaluations, selectEvaluations } from "../../../features/evaluation/evaluationsSlice";
import { fixProgressAuditCertificate } from "../../../features/progressAudit/progressAuditFixCertificateSlice";
import { fixProgressAuditCourse } from "../../../features/progressAudit/progressAuditFixCourseSlice";
import { fixProgressAuditCourseExam } from "../../../features/progressAudit/progressAuditFixCourseExamSlice";
import { fixProgressAuditLesson } from "../../../features/progressAudit/progressAuditFixLessonSlice";
import { fixProgressAuditTopicEvaluation } from "../../../features/progressAudit/progressAuditFixTopicEvaluationSlice";
import {
  fetchCertificatesListByUser,
  selectCertificateList,
  selectIsCertificateListFetching,
} from "../../../features/students/certificateListSlice";
import { getCourseMismatchFlags } from "./courseAuditUtils";
import { tracesOverlap } from "./traceOverlapUtils";
import { hasIntraTraceRegression, hasInterTraceRegression } from "./traceRegressionUtils";
import { EXIT_POINT_LABELS, BRANCH_LABELS, getActiveFlagLabels, formatDurationMs } from "./traceLabels";
import { getTraceFindings } from "./traceFindings";
import { TraceDetailPanel } from "./TraceDetailPanel";
import { buildCourseContextJson } from "./courseContextUtils";
import { computeCourseCounters, CountersStatus } from "./courseCountersUtils";
import { CountersModal } from "./CountersModal";

import styles from "./progressAudit.module.scss";

const MAX_USERS_IN_DROPDOWN = 50;
const SEARCH_DEBOUNCE_MS = 400;

const COUNTERS_LABELS: Record<CountersStatus, string> = {
  inProgress: "Counters: EN PROGRESO",
  ok: "Counters: OK",
  failed: "Counters: FAILED",
};

const COUNTERS_COLORS: Record<CountersStatus, string> = {
  inProgress: "blue",
  ok: "green",
  failed: "red",
};

interface CopyableFieldProps {
  label: string;
  value: string;
}

const CopyableField: FunctionComponent<CopyableFieldProps> = ({ label, value }) => (
  <div
    className={styles.copyableField}
    onClick={() => copyToClipboard(value)}
    title="Click para copiar"
  >
    <span className={styles.copyableFieldLabel}>{label}</span>
    <div className={styles.copyableFieldValueRow}>
      <span className={styles.copyableFieldValue} title={value}>{value}</span>
      <CogIcon size={14} icon="Copy" />
    </div>
  </div>
);

interface CourseSummaryItemProps {
  course: IProgressAuditCourseSummary;
  isSelected: boolean;
  onViewDetails: (course: IProgressAuditCourseSummary) => void;
}

// A finished course should have every lesson/topic at progress 1 - when
// it doesn't, that's exactly the kind of inconsistency the audit trail
// (progressDocumentId / ProgressAudit) exists to help explain, so it
// gets flagged visually instead of blending in with the other stats.
const CourseSummaryItem: FunctionComponent<CourseSummaryItemProps> = ({ course, isSelected, onViewDetails }) => {
  const { lessonsMismatch, topicsMismatch } = getCourseMismatchFlags(course);
  return (
    <List.Item className={cls(styles.courseItem, { [styles.courseItemSelected]: isSelected })}>
      <div className={styles.courseItemHeader}>
        <span
          className={styles.courseItemId}
          title={course.courseId}
          onClick={() => copyToClipboard(course.courseId)}
        >
          {course.courseId}
        </span>
        <Tag color={course.hasAudit ? "blue" : "default"}>
          {course.hasAudit ? 'Con auditoría' : 'Sin auditoría'}
        </Tag>
      </div>
      <div
        className={styles.courseItemProgressId}
        title={course.progressId}
        onClick={() => copyToClipboard(course.progressId)}
      >
        Progress ID: {course.progressId}
      </div>
      <div className={styles.courseItemStats}>
        <span className={cls(styles.statItem, { [styles.statMismatch]: lessonsMismatch })}>
          Lecciones: {course.lessonsFinishedCount}/{course.lessonsCount}
        </span>
        <span className={cls(styles.statItem, { [styles.statMismatch]: topicsMismatch })}>
          Tópicos: {course.topicsFinishedCount}/{course.topicsCount}
        </span>
        <span className={styles.statItem}>Evaluaciones aprobadas: {course.evaluationsPassedCount}</span>
      </div>
      <div className={styles.courseItemFooter}>
        <Tag color={course.courseFinished ? "green" : "default"}>
          {course.courseFinished ? 'Curso terminado' : 'Curso en progreso'}
        </Tag>
        <Button
          type="link"
          size="small"
          onClick={() => onViewDetails(course)}
        >
          Detalles&gt;&gt;
        </Button>
      </div>
    </List.Item>
  );
};

interface TraceBoxProps {
  trace: IProgressAuditTrace;
  isSelected: boolean;
  hasIntraRegression: boolean;
  hasInterRegression: boolean;
  onClick: (trace: IProgressAuditTrace) => void;
}

// Only one border color can be shown at a time, so when more than one
// condition applies, the more serious/specific one wins: a thrown/failed
// request first, then a regression caught by the request itself, then a
// regression only visible across requests, then simply never finishing.
const getTraceBoxVariantClass = (
  trace: IProgressAuditTrace,
  hasIntraRegression: boolean,
  hasInterRegression: boolean
): string | undefined => {
  if (trace.status === "failed") {
    return styles.traceBoxFailed;
  }
  if (hasIntraRegression) {
    return styles.traceBoxRegressedIntra;
  }
  if (hasInterRegression) {
    return styles.traceBoxRegressedInter;
  }
  if (trace.status === "started") {
    return styles.traceBoxStuck;
  }
  return undefined;
};

const TraceBox: FunctionComponent<TraceBoxProps> = ({
  trace,
  isSelected,
  hasIntraRegression,
  hasInterRegression,
  onClick,
}) => {
  const activeFlags = getActiveFlagLabels(trace.flags);
  return (
    <div
      onClick={() => onClick(trace)}
      className={cls(
        styles.traceBox,
        getTraceBoxVariantClass(trace, hasIntraRegression, hasInterRegression),
        { [styles.traceBoxSelected]: isSelected }
      )}
    >
      <div className={styles.traceBoxTopRow}>
        <span className={styles.traceBoxExitPoint}>
          {trace.exitPoint ? EXIT_POINT_LABELS[trace.exitPoint] : "En curso…"}
        </span>
        {trace.branch && <span className={styles.traceBoxBranch}>{BRANCH_LABELS[trace.branch]}</span>}
      </div>
      {activeFlags.length > 0 && (
        <div className={styles.traceBoxFlags}>
          {activeFlags.map((flag) => (
            <span key={flag} className={styles.traceBoxFlag}>{flag}</span>
          ))}
        </div>
      )}
      <div className={styles.traceBoxTime}>
        {moment(trace.receivedAt).format("HH:mm:ss")}
        {" → "}
        {trace.finishedAt ? moment(trace.finishedAt).format("HH:mm:ss") : "…"}
        {formatDurationMs(trace.durationMs) && (
          <span className={styles.traceBoxDuration}> ({formatDurationMs(trace.durationMs)})</span>
        )}
      </div>
    </div>
  );
};

export const ProgressAudit: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_MS);
  const users = useSelector(selectUsersPaginatedList);
  const isFetchingUsers = useSelector(selectAreUsersPaginatedFetching);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [showOnlyMismatches, setShowOnlyMismatches] = useState(false);
  const courses = useSelector(selectProgressAuditCourses);
  const isFetchingCourses = useSelector(selectIsFetchingProgressAuditCourses);
  const hasCoursesError = useSelector(selectHasProgressAuditCoursesError);
  const traces = useSelector(selectProgressAuditTraces) as IProgressAuditTrace[];
  const isFetchingTraces = useSelector(selectIsFetchingProgressAuditTraces);
  const hasTracesError = useSelector(selectHasProgressAuditTracesError);
  const [detailsProgressId, setDetailsProgressId] = useState<string | null>(null);
  const [selectedTrace, setSelectedTrace] = useState<IProgressAuditTrace | null>(null);
  const [pendingDeepLinkUsername, setPendingDeepLinkUsername] = useState<string | null>(null);
  const [isCopyingCourseContext, setIsCopyingCourseContext] = useState(false);
  const [isCountersModalOpen, setIsCountersModalOpen] = useState(false);
  const [fixingLessonSubdocId, setFixingLessonSubdocId] = useState<string | null>(null);
  const [fixingTopicId, setFixingTopicId] = useState<string | null>(null);
  const [isFixingCertificate, setIsFixingCertificate] = useState(false);
  const [isFixingCourse, setIsFixingCourse] = useState(false);
  const [isFixingCourseExam, setIsFixingCourseExam] = useState(false);
  const full = useSelector(selectProgressAuditFull);
  const evaluations = useSelector(selectEvaluations);
  const certificateList = useSelector(selectCertificateList) as unknown as { courseName: string; hash: string; createdAt: string }[];
  const isFetchingCertificateList = useSelector(selectIsCertificateListFetching);
  const evaluationResults = useSelector(selectProgressAuditEvaluationResults);
  const isFetchingEvaluationResults = useSelector(selectIsFetchingProgressAuditEvaluationResults);
  const history = useHistory();
  const location = useLocation();

  const counters = full ? computeCourseCounters(full) : null;
  const evaluationTitles = (evaluations as { _id: string; title: string }[]).reduce(
    (dict: Record<string, string>, evaluation) => {
      dict[evaluation._id] = evaluation.title;
      return dict;
    },
    {}
  );
  const takenLessonQuizIds = new Set(
    evaluationResults
      .filter((evaluationResult) => evaluationResult.userId === selectedUser?.username)
      .map((evaluationResult) => evaluationResult.evaluationId)
  );
  const certificateMatch = certificateList.find(
    (certificate) => certificate.courseName === (full?.course as { title?: string } | null)?.title
  );
  const certificateInfo = !isCountersModalOpen || isFetchingCertificateList
    ? null
    : { issued: !!certificateMatch, createdAt: certificateMatch?.createdAt, hash: certificateMatch?.hash };

  const onSelectTrace = (trace: IProgressAuditTrace) => {
    setSelectedTrace(trace);
    const params = new URLSearchParams(location.search);
    params.set("traceId", trace._id);
    history.replace({ search: params.toString() });
  };

  const onCopyCourseContext = async () => {
    if (!detailsProgressId || !selectedUser) {
      return;
    }
    setIsCopyingCourseContext(true);
    try {
      const context = await buildCourseContextJson(dispatch, {
        progressId: detailsProgressId,
        username: selectedUser.username,
        traces,
      });
      await copyToClipboard(JSON.stringify(context, null, 2));
    } catch (e) {
      message.error("No se pudo armar el contexto completo del curso");
    } finally {
      setIsCopyingCourseContext(false);
    }
  };

  const onOpenCountersModal = () => {
    if (!counters || !selectedUser) {
      return;
    }
    setIsCountersModalOpen(true);
    if (evaluations.length === 0) {
      dispatch(fetchEvaluations());
    }
    dispatch(fetchCertificatesListByUser(selectedUser.username));
    const lessonQuizIds = counters.topics
      .flatMap((topic) => topic.lessons)
      .filter((lesson) => lesson.hasQuiz && lesson.quizEvaluationId)
      .map((lesson) => lesson.quizEvaluationId as string);
    if (lessonQuizIds.length > 0) {
      dispatch(fetchProgressAuditEvaluationResults(selectedUser.username, lessonQuizIds));
    } else {
      dispatch(clearProgressAuditEvaluationResults());
    }
  };

  const onFixLesson = (progressLessonSubdocId: string) => {
    if (!detailsProgressId) {
      return;
    }
    setFixingLessonSubdocId(progressLessonSubdocId);
    dispatch(
      fixProgressAuditLesson(
        detailsProgressId,
        progressLessonSubdocId,
        () => {
          message.success("Lección corregida");
          // Refresca el bundle (Counters) y las trazas (la traza
          // fixedByAdmin nueva) para reflejar el cambio.
          dispatch(fetchProgressAuditFullByProgressId(detailsProgressId));
          dispatch(fetchProgressAuditTracesByProgressId(detailsProgressId));
          setFixingLessonSubdocId(null);
        },
        () => {
          message.error("No se pudo corregir la lección");
          setFixingLessonSubdocId(null);
        }
      )
    );
  };

  const onFixTopicEvaluation = (topicId: string, evaluationId: string) => {
    if (!detailsProgressId) {
      return;
    }
    setFixingTopicId(topicId);
    dispatch(
      fixProgressAuditTopicEvaluation(
        detailsProgressId,
        topicId,
        evaluationId,
        () => {
          message.success("Evaluación del tópico corregida");
          dispatch(fetchProgressAuditFullByProgressId(detailsProgressId));
          dispatch(fetchProgressAuditTracesByProgressId(detailsProgressId));
          setFixingTopicId(null);
        },
        (errorMessage: string) => {
          message.error(errorMessage);
          setFixingTopicId(null);
        }
      )
    );
  };

  const onFixCertificate = () => {
    if (!detailsProgressId || !selectedUser) {
      return;
    }
    setIsFixingCertificate(true);
    dispatch(
      fixProgressAuditCertificate(
        detailsProgressId,
        () => {
          message.success("Certificado creado");
          dispatch(fetchProgressAuditFullByProgressId(detailsProgressId));
          dispatch(fetchProgressAuditTracesByProgressId(detailsProgressId));
          dispatch(fetchCertificatesListByUser(selectedUser.username));
          setIsFixingCertificate(false);
        },
        (errorMessage: string) => {
          message.error(errorMessage);
          setIsFixingCertificate(false);
        }
      )
    );
  };

  const onFixCourseExam = (evaluationId: string) => {
    if (!detailsProgressId) {
      return;
    }
    setIsFixingCourseExam(true);
    dispatch(
      fixProgressAuditCourseExam(
        detailsProgressId,
        evaluationId,
        () => {
          message.success("Examen final del curso corregido");
          dispatch(fetchProgressAuditFullByProgressId(detailsProgressId));
          dispatch(fetchProgressAuditTracesByProgressId(detailsProgressId));
          setIsFixingCourseExam(false);
        },
        (errorMessage: string) => {
          message.error(errorMessage);
          setIsFixingCourseExam(false);
        }
      )
    );
  };

  const onFixCourse = () => {
    if (!detailsProgressId || !selectedUser) {
      return;
    }
    setIsFixingCourse(true);
    dispatch(
      fixProgressAuditCourse(
        detailsProgressId,
        () => {
          message.success("Curso marcado como terminado");
          dispatch(fetchProgressAuditFullByProgressId(detailsProgressId));
          dispatch(fetchProgressAuditTracesByProgressId(detailsProgressId));
          dispatch(fetchCertificatesListByUser(selectedUser.username));
          setIsFixingCourse(false);
        },
        (errorMessage: string) => {
          message.error(errorMessage);
          setIsFixingCourse(false);
        }
      )
    );
  };

  useEffect(() => {
    dispatch(fetchUsersPaginated({
      text: debouncedSearchText || undefined,
      limit: MAX_USERS_IN_DROPDOWN,
    }));
  }, [dispatch, debouncedSearchText]);

  // Deep-link support: a shared link with ?username=...&progressId=...&traceId=...
  // loads that user's courses and that course's trace history (pre-selecting
  // the trace) without needing to search for the user first - only runs
  // once, on mount. The username is resolved through the same paginated
  // users endpoint the search box uses, so it also ends up as a normal
  // option the Select can show as selected.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const username = params.get("username");
    const progressId = params.get("progressId");
    if (username) {
      setSearchText(username);
      setPendingDeepLinkUsername(username);
    }
    if (progressId) {
      setDetailsProgressId(progressId);
      dispatch(fetchProgressAuditTracesByProgressId(progressId));
      dispatch(fetchProgressAuditFullByProgressId(progressId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingDeepLinkUsername) {
      return;
    }
    const match = (users as IUser[]).find((u) => u.username === pendingDeepLinkUsername);
    if (match) {
      setSelectedUser(match);
      dispatch(fetchProgressAuditCoursesByUsername(match.username));
      setPendingDeepLinkUsername(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, pendingDeepLinkUsername]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const traceId = params.get("traceId");
    if (!traceId || traces.length === 0) {
      return;
    }
    const match = traces.find((trace) => trace._id === traceId);
    if (match) {
      setSelectedTrace(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traces]);

  const onSelectUser = (username: string) => {
    const user = (users as IUser[]).find((u) => u.username === username) || null;
    setSelectedUser(user);
    setDetailsProgressId(null);
    setSelectedTrace(null);
    setIsCountersModalOpen(false);
    dispatch(clearProgressAuditTraces());
    dispatch(clearProgressAuditFull());
    dispatch(clearProgressAuditEvaluationResults());
    history.replace({ search: user ? `username=${encodeURIComponent(user.username)}` : "" });
    if (user) {
      dispatch(fetchProgressAuditCoursesByUsername(user.username));
    } else {
      dispatch(clearProgressAuditCourses());
    }
  };

  const onViewCourseDetails = (course: IProgressAuditCourseSummary) => {
    setDetailsProgressId(course.progressId);
    setSelectedTrace(null);
    setIsCountersModalOpen(false);
    dispatch(fetchProgressAuditTracesByProgressId(course.progressId));
    dispatch(fetchProgressAuditFullByProgressId(course.progressId));
    dispatch(clearProgressAuditEvaluationResults());
    const params = new URLSearchParams(location.search);
    params.set("progressId", course.progressId);
    params.delete("traceId");
    history.replace({ search: params.toString() });
  };

  const displayedCourses = showOnlyMismatches
    ? (courses as IProgressAuditCourseSummary[]).filter((course) => getCourseMismatchFlags(course).hasMismatch)
    : (courses as IProgressAuditCourseSummary[]);

  const traceFindings = getTraceFindings(traces);

  const [coursesListRef, coursesListHeight] = useAvailableHeight<HTMLDivElement>([
    selectedUser,
    hasCoursesError,
  ]);
  const [traceGraphRef, traceGraphHeight] = useAvailableHeight<HTMLDivElement>([
    detailsProgressId,
    hasTracesError,
    traceFindings.length,
  ]);

  return (
    <MainLayout
      title="Auditoría de Progreso"
      breadcrumbs={['Usuarios', 'Auditoría de Progreso']}
      rightTopOptions={
        <Button
          type="primary"
          icon={<CogIcon size={14} icon="Link" color="#fff" className={styles.shareButtonIcon} />}
          onClick={() => copyToClipboard(window.location.href)}
        >
          Compartir
        </Button>
      }
    >
      <Row gutter={{ xs: 16, lg: 24 }} className={styles.container}>
        <Col xs={24} lg={9} className={styles.leftPanel}>
          <Select
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder="Buscar usuario por nombre o username"
            filterOption={false}
            loading={isFetchingUsers}
            value={selectedUser ? selectedUser.username : undefined}
            onSearch={setSearchText}
            onChange={onSelectUser}
          >
            {(users as IUser[]).map((user) => (
              <Select.Option key={user.username} value={user.username}>
                {user.name} ({user.username})
              </Select.Option>
            ))}
          </Select>
          {selectedUser && (
            <div className={styles.selectedUserCard}>
              <CopyableField label="ID de usuario" value={selectedUser.username} />
              <CopyableField label="Correo electrónico" value={selectedUser.email} />
            </div>
          )}
          {selectedUser && (
            <div className={styles.mismatchFilter}>
              <Switch
                size="small"
                checked={showOnlyMismatches}
                onChange={setShowOnlyMismatches}
              />
              <span>Mostrar solo cursos con errores</span>
            </div>
          )}
          {selectedUser && hasCoursesError && (
            <Alert
              className={styles.coursesError}
              type="error"
              message="No se pudo cargar el progreso de este usuario."
              showIcon
            />
          )}
          {selectedUser && !hasCoursesError && (
            <Spin spinning={isFetchingCourses}>
              <div
                ref={coursesListRef}
                className={styles.coursesListWrapper}
                style={{ height: coursesListHeight }}
              >
                <List<IProgressAuditCourseSummary>
                  className={styles.coursesList}
                  dataSource={displayedCourses}
                  renderItem={(course) => (
                    <CourseSummaryItem
                      key={course.progressId}
                      course={course}
                      isSelected={detailsProgressId === course.progressId}
                      onViewDetails={onViewCourseDetails}
                    />
                  )}
                />
              </div>
            </Spin>
          )}
        </Col>
        <Col xs={24} lg={15} className={styles.rightPanel}>
          {!detailsProgressId && (
            <div className={styles.tracesPlaceholder}>
              Hacé click en &ldquo;Detalles&gt;&gt;&rdquo; en un curso para ver su historial de ejecuciones.
            </div>
          )}
          {detailsProgressId && (
            <Spin spinning={isFetchingTraces}>
              {hasTracesError ? (
                <Alert
                  className={styles.tracesError}
                  type="error"
                  message="No se pudo cargar el historial de este curso."
                  showIcon
                />
              ) : (
                <div className={styles.traceGraphWrapper}>
                  <div className={styles.traceFindingsHeader}>
                    <div className={styles.traceFindings}>
                      {traces.length === 0 ? (
                        <div className={cls(styles.traceFinding, styles.traceFindingWarning)}>
                          Sin trazas de auditoría para este curso.
                        </div>
                      ) : traceFindings.length === 0 ? (
                        <div className={cls(styles.traceFinding, styles.traceFindingOk)}>Todo OK</div>
                      ) : (
                        <ul className={styles.traceFindingsList}>
                          {traceFindings.map((finding, index) => (
                            <li
                              key={index}
                              className={cls(styles.traceFinding, {
                                [styles.traceFindingError]: finding.severity === "error",
                                [styles.traceFindingWarning]: finding.severity === "warning",
                              })}
                            >
                              {finding.message}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className={styles.traceFindingsActions}>
                      {counters && (
                        <Tag
                          className={styles.countersTag}
                          color={COUNTERS_COLORS[counters.status]}
                          onClick={onOpenCountersModal}
                        >
                          {COUNTERS_LABELS[counters.status]}
                        </Tag>
                      )}
                      <Button
                        type="link"
                        size="small"
                        className={styles.copyCourseContextButton}
                        disabled={isCopyingCourseContext}
                        icon={<CogIcon size={14} icon={isCopyingCourseContext ? "loading" : "Download"} />}
                        onClick={onCopyCourseContext}
                      >
                        Copiar contexto completo
                      </Button>
                    </div>
                  </div>
                  <div className={styles.traceLegend}>
                    <span className={styles.legendItem}>
                      <span className={cls(styles.legendSwatchBorder, styles.legendBorderDefault)} />
                      Normal
                    </span>
                    <span className={styles.legendItem}>
                      <span className={cls(styles.legendSwatchBorder, styles.legendBorderFailed)} />
                      Falló
                    </span>
                    <span className={styles.legendItem}>
                      <span className={cls(styles.legendSwatchBorder, styles.legendBorderStuck)} />
                      Sin terminar
                    </span>
                    <span className={styles.legendItem}>
                      <span className={cls(styles.legendSwatchBorder, styles.legendBorderRegressedIntra)} />
                      Regresión en la misma traza
                    </span>
                    <span className={styles.legendItem}>
                      <span className={cls(styles.legendSwatchBorder, styles.legendBorderRegressedInter)} />
                      Regresión entre trazas
                    </span>
                    <span className={styles.legendItem}>
                      <span className={styles.legendSwatchLine} />
                      Solapamiento
                    </span>
                  </div>
                  <div
                    ref={traceGraphRef}
                    className={styles.traceGraphBody}
                    style={{ height: traceGraphHeight }}
                  >
                    <div className={styles.traceScroll}>
                      <div className={styles.traceColumn}>
                        {traces.map((trace, index) => {
                          const nextTrace = traces[index + 1];
                          const overlapsNext = nextTrace ? tracesOverlap(trace, nextTrace) : false;
                          return (
                            <React.Fragment key={trace._id}>
                              <TraceBox
                                trace={trace}
                                isSelected={selectedTrace?._id === trace._id}
                                hasIntraRegression={hasIntraTraceRegression(trace)}
                                hasInterRegression={hasInterTraceRegression(traces, index)}
                                onClick={onSelectTrace}
                              />
                              {nextTrace && (
                                <div className={cls(styles.traceConnector, { [styles.traceConnectorOverlap]: overlapsNext })} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                    {selectedTrace ? (
                      <TraceDetailPanel trace={selectedTrace} />
                    ) : (
                      <div className={cls(styles.traceDetailPanel, styles.traceDetailPlaceholder)}>
                        Hacé click en una traza para ver su detalle.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Spin>
          )}
        </Col>
      </Row>
      <CountersModal
        visible={isCountersModalOpen}
        onClose={() => setIsCountersModalOpen(false)}
        counters={counters}
        evaluationTitles={evaluationTitles}
        takenLessonQuizIds={takenLessonQuizIds}
        isLoadingDetail={isCountersModalOpen && (isFetchingCertificateList || isFetchingEvaluationResults)}
        certificate={certificateInfo}
        onFixLesson={onFixLesson}
        fixingLessonSubdocId={fixingLessonSubdocId}
        onFixTopicEvaluation={onFixTopicEvaluation}
        fixingTopicId={fixingTopicId}
        onFixCertificate={onFixCertificate}
        isFixingCertificate={isFixingCertificate}
        onFixCourse={onFixCourse}
        isFixingCourse={isFixingCourse}
        onFixCourseExam={onFixCourseExam}
        isFixingCourseExam={isFixingCourseExam}
      />
    </MainLayout>
  );
};
